/**
 * Estrutura canónica de dias da rota publicada.
 *
 * Regras vinculativas:
 * - uma viagem de N dias publica exatamente Dia 1..Dia N, cada `dayIndex` uma
 *   única vez e por ordem;
 * - cada `scheduleEntryId` existe uma única vez, num único dia;
 * - dias sem visitas continuam a existir (tempo livre / logística);
 * - uma entrada sem horas válidas nunca é publicada.
 *
 * Módulo puro: sem IO, sem React, sem relógio.
 */

import type { DayWindow } from "./day-windows";

export type RouteDayEntry = {
  scheduleEntryId: string;
  day: number;
  startAt: Date;
  endAt: Date;
  kind: "stop" | "break";
};

export type RouteDay<E extends RouteDayEntry = RouteDayEntry> = {
  dayIndex: number;
  localDate?: string;
  startMinutes: number;
  endMinutes: number;
  entries: E[];
};

export type RouteDayIssue =
  | { code: "duplicate_day"; day: number }
  | { code: "missing_day"; day: number }
  | { code: "duplicate_entry_id"; entryId: string }
  | { code: "entry_without_day"; entryId: string; day: number }
  | { code: "entry_without_time"; entryId: string }
  | { code: "entry_outside_day"; entryId: string; day: number };

const DEFAULT_START = 9 * 60;
const DEFAULT_END = 22 * 60;

/** Uma entrada só é publicável com identidade, dia e horas coerentes. */
export function isPublishableEntry(e: {
  scheduleEntryId?: string;
  day?: number;
  startAt?: Date;
  endAt?: Date;
}): boolean {
  if (!e.scheduleEntryId) return false;
  if (!Number.isFinite(e.day) || (e.day as number) < 1) return false;
  const s = e.startAt instanceof Date ? e.startAt.getTime() : Number.NaN;
  const t = e.endAt instanceof Date ? e.endAt.getTime() : Number.NaN;
  if (!Number.isFinite(s) || !Number.isFinite(t)) return false;
  return t > s;
}

/**
 * Constrói os dias UMA única vez. Quando existem `dayWindows`, são elas a
 * definir os dias; sem elas, os dias vêm das próprias entradas (1..max).
 */
export function buildRouteDays<E extends RouteDayEntry>(input: {
  entries: readonly E[];
  dayWindows?: readonly DayWindow[];
}): RouteDay<E>[] {
  const maxEntryDay = input.entries.reduce((m, e) => Math.max(m, Math.trunc(e.day || 1)), 1);
  const maxWindowDay = (input.dayWindows ?? []).reduce((m, w) => Math.max(m, w.day), 0);
  const total = Math.max(1, maxEntryDay, maxWindowDay);
  const byDay = new Map<number, E[]>();
  for (const e of input.entries) {
    const day = Math.max(1, Math.trunc(e.day || 1));
    const list = byDay.get(day) ?? [];
    list.push(e);
    byDay.set(day, list);
  }
  const days: RouteDay<E>[] = [];
  for (let dayIndex = 1; dayIndex <= total; dayIndex++) {
    const w = (input.dayWindows ?? []).find((x) => x.day === dayIndex);
    days.push({
      dayIndex,
      ...(w?.localDate ? { localDate: w.localDate } : {}),
      startMinutes: w?.startMinutes ?? DEFAULT_START,
      endMinutes: w?.endMinutes ?? DEFAULT_END,
      entries: (byDay.get(dayIndex) ?? []).sort((a, b) => a.startAt.getTime() - b.startAt.getTime()),
    });
  }
  return days;
}

/**
 * Validador de publicação: devolve TODOS os problemas encontrados. Um draft
 * com qualquer problema não pode substituir a rota visível.
 */
export function validateRouteDays<E extends RouteDayEntry>(
  days: readonly RouteDay<E>[],
  entries: readonly E[],
): RouteDayIssue[] {
  const issues: RouteDayIssue[] = [];
  const seenDays = new Set<number>();
  for (const d of days) {
    if (seenDays.has(d.dayIndex)) issues.push({ code: "duplicate_day", day: d.dayIndex });
    seenDays.add(d.dayIndex);
  }
  const max = days.reduce((m, d) => Math.max(m, d.dayIndex), 0);
  for (let day = 1; day <= max; day++) {
    if (!seenDays.has(day)) issues.push({ code: "missing_day", day });
  }

  const seenIds = new Set<string>();
  for (const e of entries) {
    if (!isPublishableEntry(e)) {
      issues.push({ code: "entry_without_time", entryId: e.scheduleEntryId ?? "?" });
      continue;
    }
    if (seenIds.has(e.scheduleEntryId)) issues.push({ code: "duplicate_entry_id", entryId: e.scheduleEntryId });
    seenIds.add(e.scheduleEntryId);
    if (!seenDays.has(Math.trunc(e.day))) {
      issues.push({ code: "entry_without_day", entryId: e.scheduleEntryId, day: e.day });
    }
  }

  // Cada entrada aparece exatamente uma vez, no seu próprio dia.
  const placed = new Map<string, number>();
  for (const d of days) {
    for (const e of d.entries) {
      if (placed.has(e.scheduleEntryId)) {
        issues.push({ code: "duplicate_entry_id", entryId: e.scheduleEntryId });
      }
      placed.set(e.scheduleEntryId, d.dayIndex);
      if (Math.trunc(e.day) !== d.dayIndex) {
        issues.push({ code: "entry_outside_day", entryId: e.scheduleEntryId, day: d.dayIndex });
      }
    }
  }
  return issues;
}
